import { getUID } from '@redviking/argonaut-util/src/getUID';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import type { UiNodeSpec } from './UiNodeSpec';
import type { DeployParamKind } from '@redviking/argonaut-util/src/mes/deployParam.zod';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import type { UiFlowNodeControl } from '../../controls/UiFlowNodeControl';

export const sparkplugCommanderUiNodeSpec: UiNodeSpec<'sparkplugCommander'> = {
    requiredSockets: [ 'trigger' ],
    cfg: () => ({
        disabled: false,
        controlValues: {
            configDeploymentParameter: {
                deployParamId: '',
                deployParamKind: 'sparkplugNodeCfg' as DeployParamKind.sparkplugNodeCfg,
                type: 'deployParam',
            },
            metricExpressions: [],
            mqttBrokerId: '',
        },
        id: getUID(),
        kind: 'sparkplugCommander',
    }),
    controls: getNodeCfg => ({
        configDeploymentParameter: { cfg: { kind: 'deploymentParameter', label: 'Deployment Parameter' }, nodeCfg: getNodeCfg(), value: getNodeCfg().controlValues.configDeploymentParameter },
        metricExpressions: {
            cfg: {
                kind: 'button',
                label: 'Add Metric',
                onClick: () => {
                    const nodeCfg = getNodeCfg();
                    createNodeSocket({
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                metricExpressions: [
                                    ...nodeCfg.controlValues.metricExpressions,
                                    '',
                                ],
                            },
                        },
                    });
                },
            },
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.metricExpressions,
        },
    }),
    inputs: nodeCfg => {
        const inputs: ReturnType<UiNodeSpec<'sparkplugCommander'>['inputs']> = {
            trigger: { socket: 'event', label: 'Trigger' },
        };

        for (let i = 0; i < nodeCfg.controlValues.metricExpressions.length; i++) {
            const control: UiFlowNodeControl.Opts<string> = {
                nodeCfg,
                socketDefault: false,
                value: nodeCfg.controlValues.metricExpressions[i],
                cfg: {
                    kind: 'deletableText',
                    placeholder: 'Metric or Metric Expression',
                    getValue: nodeCfg => {
                        if (nodeCfg.kind !== 'sparkplugCommander') {
                            return null;
                        }
                        return nodeCfg.controlValues.metricExpressions[i];
                    },
                    onChange: newValue => {
                        const newMetricExpressions = [
                            ...nodeCfg.controlValues.metricExpressions,
                        ];
                        newMetricExpressions[i] = newValue;

                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    metricExpressions: newMetricExpressions,
                                },
                            },
                        });
                    },
                    onDelete () {
                        removeNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    metricExpressions: [
                                        ...nodeCfg.controlValues.metricExpressions.slice(0, i),
                                        ...nodeCfg.controlValues.metricExpressions.slice(i + 1),
                                    ],
                                },
                            },
                            direction: 'input',
                            removalMethod: {
                                index: i,
                                prefix: 'input',
                                kind: 'indexedIdentifier',
                            },
                        });
                    },
                },
            };
            inputs[`input${i}`] = { control, socket: 'varValue' };
        }
        return inputs;
    },
    kindLabel: 'Sparkplug Commander',
    nodeCategory: 'external',
    outputs: () => ({
        event: { socket: 'event', label: 'Next' },
    }),
    validator: node => {
        const errors: Record<string, string> = {};
        if (node.controlValues.metricExpressions.length === 0) {
            errors.metricExpressions = 'At least one metric expression is required';
        }
        if (!node.controlValues.configDeploymentParameter.deployParamId) {
            errors.configDeploymentParameter = 'Sparkplug Deployment Parameter is required';
        }
        return Object.keys(errors).length ? errors : true;
    },
};
